import type { KeyboardEvent } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePrivy, useWallets } from '@privy-io/react-auth'
import { WalletProfileButton } from '@/components/WalletProfileButton'
import { TipModal } from '@/components/TipModal'
import { shouldAutoOpenTip } from '@/lib/tipNudge'
import { pickEthereumSigningWallet } from '@/lib/privyWallet'
import { shortWalletAddress } from '@/lib/walletDisplayName'

const POETRY_KEEPING_WHOLE_URL = 'https://poetrysociety.org/poetry-in-motion/keeping-things-whole'
const ILLUST_HOME_URL = 'https://illust.space'

export function RememberHome() {
  const navigate = useNavigate()
  const { ready, authenticated, login, logout } = usePrivy()
  const { wallets } = useWallets()
  const signingWallet = ready ? pickEthereumSigningWallet(wallets) : undefined
  const connectedAddress = signingWallet?.address
  const disconnectLabel = useMemo(() => {
    if (!connectedAddress) return 'wallet'
    try {
      return shortWalletAddress(connectedAddress)
    } catch {
      return 'wallet'
    }
  }, [connectedAddress])
  const [tipOpen, setTipOpen] = useState(false)

  useEffect(() => {
    if (shouldAutoOpenTip()) setTipOpen(true)
  }, [])

  const handlePoetryLinkKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
  }

  const handleIllustLinkKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.currentTarget.click()
    }
  }

  return (
    <div className="mem-page mem-page--center">
      <header className="mem-header">
        <div className="mem-header-start">
          <WalletProfileButton />
          <span className="mem-brand">Memoria</span>
        </div>
        <div className="mem-header-end">
          <button
            type="button"
            onClick={() => setTipOpen(true)}
            className="mem-btn mem-btn--ghost"
            aria-label="Tip the developers"
            title="Tip the developers"
            style={{ padding: '8px 10px' }}
          >
            ◉
          </button>
          <button type="button" onClick={() => navigate('/map')} className="mem-btn mem-btn--ghost">
            Map
          </button>
        </div>
      </header>

      <main className="mem-main">
        <div className="mem-main__center-block">
          <img
            src="/branding/memoria-logo.png"
            alt="Memoria — don't rent reality. make it bloom."
            className="mem-home-logo"
            width={500}
            height={323}
            decoding="async"
          />
          <p
            className="mem-display mem-subtitle"
            style={{
              marginBottom: 8,
              fontSize: '0.82rem',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'var(--mem-text-dim)',
            }}
          >
            Onchain memories
          </p>
          <h1 className="mem-title-xl mem-display">Keeping things whole.</h1>
          <p className="mem-home-body">
            Mint a memory, leave a message, plant a seed, sow a reminder anchored in time and space. Find
            it again with the map, or keep it private. Preserve it for pretty much eternity. Onchain, owned
            by you and always evolving. Why keeping things{' '}
            <a
              href={POETRY_KEEPING_WHOLE_URL}
              target="_blank"
              rel="noopener noreferrer"
              tabIndex={0}
              aria-label="Keeping Things Whole, poem by Mark Strand on Poetry Society"
              onKeyDown={handlePoetryLinkKeyDown}
            >
              whole
            </a>
            ?
          </p>

          <div className="mem-actions">
            <button type="button" className="mem-btn mem-btn--primary mem-btn--mint" onClick={() => navigate('/camera')}>
              Mint memory
            </button>

            {!authenticated ? (
              <button
                type="button"
                className="mem-btn mem-btn--secondary"
                onClick={() => {
                  void login()
                }}
                disabled={!ready}
              >
                {ready ? 'Connect wallet / log in' : 'Loading…'}
              </button>
            ) : (
              <button
                type="button"
                className="mem-btn mem-btn--secondary"
                onClick={() => {
                  document.body.classList.remove('mem-wallet-ready')
                  void logout()
                }}
              >
                Disconnect ({disconnectLabel})
              </button>
            )}
          </div>
        </div>

        <footer className="mem-home-footer">
          <p className="mem-home-footer__tagline">an illust pathfinder project</p>
          <a
            href={ILLUST_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mem-home-footer__logo-link"
            aria-label="illust — visit illust.space"
            tabIndex={0}
            onKeyDown={handleIllustLinkKeyDown}
          >
            <img
              src="/Illust_Base_LogoWhite.png"
              alt="illust."
              className="mem-home-footer__logo"
              width={120}
              height={30}
              decoding="async"
            />
          </a>
        </footer>
      </main>
      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} />
    </div>
  )
}
